import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { KantoorSidebar } from "@/components/KantoorSidebar";
import { MobileBottomTabs } from "@/components/MobileBottomTabs";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { LogOut, Sparkles, MessageSquareText, CheckCircle2, Clock } from "lucide-react";

const StatusBadge = ({ status, t }) => {
  const map = {
    nieuw: { label: t("dashboard.status.nieuw"), cls: "bg-amber-100 text-[#B7740A]" },
    gesprek_gepland: { label: t("dashboard.status.gesprek_gepland"), cls: "bg-blue-100 text-blue-800" },
    match: { label: t("dashboard.status.match"), cls: "bg-emerald-100 text-emerald-800" },
    geen_match: { label: t("dashboard.status.geen_match"), cls: "bg-rose-100 text-rose-800" },
  };
  const m = map[status] || map.nieuw;
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`}>{m.label}</span>;
};

const Overview = ({ matches, t, lang }) => {
  const newMatches = matches.filter((m) => m.status === "nieuw");
  const gesprekken = matches.filter((m) => m.status === "gesprek_gepland");
  const wonMatches = matches.filter((m) => m.status === "match");
  const totalRevenue = wonMatches.reduce((acc, m) => acc + (m.vergoeding || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1A2B4A]">{t("dashboard.title")}</h1>
        <p className="text-slate-600 mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: t("dashboard.stat_new"), value: newMatches.length, icon: Sparkles, color: "text-[#F5A623] bg-[#F5A623]/10", testid: "stat-new" },
          { label: t("dashboard.stat_planned"), value: gesprekken.length, icon: Clock, color: "text-blue-600 bg-blue-100", testid: "stat-gesprekken" },
          { label: t("dashboard.stat_won"), value: wonMatches.length, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-100", testid: "stat-won" },
          { label: t("dashboard.stat_revenue"), value: `€${totalRevenue.toFixed(0)}`, icon: MessageSquareText, color: "text-[#1A2B4A] bg-slate-100", testid: "stat-revenue" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="beecenter-card p-5" data-testid={s.testid}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <Icon size={20} />
              </div>
              <div className="mt-3 text-2xl md:text-3xl font-heading font-bold text-[#1A2B4A]">{s.value}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="beecenter-card overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-heading font-bold text-[#1A2B4A]">{t("dashboard.new_matches")}</h2>
          <Link to="/dashboard/matches" className="text-sm font-semibold text-[#F5A623] hover:underline" data-testid="link-all-matches">{t("dashboard.show_all")} →</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {newMatches.slice(0, 5).map((m) => (
            <Link to={`/dashboard/matches/${m.id}`} key={m.id} className="flex items-center gap-4 p-5 hover:bg-slate-50 transition-colors" data-testid={`row-match-${m.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#1A2B4A] text-white flex items-center justify-center font-bold">
                {m.klant?.bedrijfsnaam?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A2B4A] truncate">{m.klant?.bedrijfsnaam}</div>
                <div className="text-xs text-slate-500 truncate">{m.klant?.branche} · {m.klant?.medewerkers} · {m.klant?.locatie}</div>
              </div>
              <StatusBadge status={m.status} t={t} />
            </Link>
          ))}
          {newMatches.length === 0 && (
            <div className="p-10 text-center text-slate-500 text-sm">{t("dashboard.no_new")}</div>
          )}
        </div>
      </div>

      <div className="beecenter-card overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-heading font-bold text-[#1A2B4A]">{t("dashboard.previous_matches")}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.company")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.match_date")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.status")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.gesprek")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("dashboard.table.result")}</th>
                <th className="text-right px-5 py-3 font-semibold">{t("dashboard.table.fee")}</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`table-row-${m.id}`}>
                  <td className="px-5 py-4 font-semibold text-[#1A2B4A]">{m.klant?.bedrijfsnaam}</td>
                  <td className="px-5 py-4 text-slate-600">{new Date(m.created_at).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" })}</td>
                  <td className="px-5 py-4"><StatusBadge status={m.status} t={t} /></td>
                  <td className="px-5 py-4 text-slate-600">{m.gesprek ? `${m.gesprek.datum} · ${m.gesprek.tijdstip}` : "—"}</td>
                  <td className="px-5 py-4 text-slate-600">{m.status === "match" ? t("dashboard.result_match") : m.status === "geen_match" ? t("dashboard.result_no_match") : "—"}</td>
                  <td className="px-5 py-4 text-right font-semibold text-[#1A2B4A]">{m.vergoeding ? `€${m.vergoeding.toFixed(0)}` : "—"}</td>
                </tr>
              ))}
              {matches.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-slate-500">{t("dashboard.no_matches")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const MatchesList = ({ matches, t }) => (
  <div>
    <h1 className="font-heading text-3xl font-bold text-[#1A2B4A]">{t("dashboard.matches_title")}</h1>
    <p className="text-slate-600 mt-1">{t("dashboard.matches_subtitle")}</p>
    <div className="mt-6 grid md:grid-cols-2 gap-4">
      {matches.filter((m) => m.status === "nieuw").map((m) => (
        <Link to={`/dashboard/matches/${m.id}`} key={m.id} className="beecenter-card p-5 hover:shadow-md transition-all" data-testid={`match-tile-${m.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#1A2B4A] text-white flex items-center justify-center font-bold text-lg">{m.klant?.bedrijfsnaam?.[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1A2B4A]">{m.klant?.bedrijfsnaam}</div>
              <div className="text-xs text-slate-500 mt-0.5">{m.klant?.branche}</div>
              <div className="text-xs text-slate-500">{m.klant?.medewerkers} · {m.klant?.locatie}</div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(m.klant?.behoeften || []).slice(0, 3).map((b) => <span key={b} className="pill-amber">{b}</span>)}
              </div>
            </div>
          </div>
        </Link>
      ))}
      {matches.filter((m) => m.status === "nieuw").length === 0 && (
        <div className="md:col-span-2 beecenter-card p-12 text-center text-slate-500">{t("dashboard.matches_empty")}</div>
      )}
    </div>
  </div>
);

const GesprekkenList = ({ matches, t, lang }) => {
  const list = matches.filter((m) => m.gesprek);
  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#1A2B4A]">{t("dashboard.gesprekken_title")}</h1>
      <p className="text-slate-600 mt-1">{t("dashboard.gesprekken_subtitle")}</p>
      <div className="mt-6 beecenter-card overflow-hidden">
        <div className="divide-y divide-slate-100">
          {list.map((m) => (
            <Link to={`/dashboard/matches/${m.id}`} key={m.id} className="p-5 flex items-center gap-4 hover:bg-slate-50" data-testid={`gesprek-row-${m.id}`}>
              <div className="w-12 h-12 rounded-lg bg-[#F5A623]/10 text-[#F5A623] flex flex-col items-center justify-center font-bold">
                <span className="text-xs">{new Date(m.gesprek.datum).toLocaleDateString(lang, { month: "short" }).toUpperCase()}</span>
                <span className="text-base leading-none">{new Date(m.gesprek.datum).getDate()}</span>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-[#1A2B4A]">{m.klant?.bedrijfsnaam}</div>
                <div className="text-xs text-slate-500">{m.gesprek.tijdstip} · {m.gesprek.via} · {m.gesprek.duur_minuten} min</div>
              </div>
              <StatusBadge status={m.status} t={t} />
            </Link>
          ))}
          {list.length === 0 && <div className="p-12 text-center text-slate-500">{t("dashboard.gesprekken_empty")}</div>}
        </div>
      </div>
    </div>
  );
};

const Placeholder = ({ title, desc }) => (
  <div className="beecenter-card p-12 text-center">
    <h1 className="font-heading text-2xl font-bold text-[#1A2B4A]">{title}</h1>
    <p className="text-slate-600 mt-2 max-w-md mx-auto">{desc}</p>
  </div>
);

const Dashboard = ({ section = "overview" }) => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role === "klant") { navigate("/mijn-matches"); return; }
    api.get("/matches/me").then((r) => setMatches(r.data)).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#F5A623] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const newCount = matches.filter((m) => m.status === "nieuw").length;
  const gesprekkenCount = matches.filter((m) => m.gesprek && m.status === "gesprek_gepland").length;
  const lang = i18n.language?.startsWith("en") ? "en-US" : "nl-NL";

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <KantoorSidebar badges={{ newMatches: newCount, newGesprekken: gesprekkenCount }} />
      <main className="flex-1 min-w-0">
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <Logo size="sm" />
          <button onClick={logout} className="p-2 text-slate-600" data-testid="mobile-dash-logout"><LogOut size={18} /></button>
        </div>
        <div className="p-4 md:p-8 pb-24 lg:pb-8 max-w-6xl mx-auto">
          {section === "overview" && <Overview matches={matches} t={t} lang={lang} />}
          {section === "matches" && <MatchesList matches={matches} t={t} />}
          {section === "gesprekken" && <GesprekkenList matches={matches} t={t} lang={lang} />}
          {section === "klanten" && <Placeholder title={t("dashboard.placeholder.klanten_title")} desc={t("dashboard.placeholder.klanten_desc")} />}
          {section === "agenda" && <Placeholder title={t("dashboard.placeholder.agenda_title")} desc={t("dashboard.placeholder.agenda_desc")} />}
          {section === "berichten" && <Placeholder title={t("dashboard.placeholder.berichten_title")} desc={t("dashboard.placeholder.berichten_desc")} />}
          {section === "facturatie" && <Placeholder title={t("dashboard.placeholder.facturatie_title")} desc={t("dashboard.placeholder.facturatie_desc")} />}
          {section === "instellingen" && <Placeholder title={t("dashboard.placeholder.instellingen_title")} desc={t("dashboard.placeholder.instellingen_desc")} />}
        </div>
        <MobileBottomTabs badges={{ newMatches: newCount, newGesprekken: gesprekkenCount }} />
      </main>
    </div>
  );
};

export default Dashboard;
